<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

import { cardActions } from "@/action/cardActions";
import { getBoardIdsOfGroupedStickies } from "@/backend/read/BackendReader";
import { cardKey, cardMetaKey, cardMethodsKey } from "@/components/StickyNote/injectKeys";
import { actionSourceKey } from "@/components/action/injectKeys";
import BaseButton from "@/components/ui/BaseButton/BaseButton.vue";
import ListItem from "@/components/ui/ComplexList/ListItem.vue";
import { dropdownKey } from "@/components/ui/DropdownMenu/injectKeys";
import IconButton from "@/components/ui/IconButton/IconButton.vue";
import MenuList from "@/components/ui/MenuList/MenuList.vue";
import MenuListHeader from "@/components/ui/MenuList/MenuListHeader.vue";
import MenuListItems from "@/components/ui/MenuList/MenuListItems.vue";
import MenuListTitle from "@/components/ui/MenuList/MenuListTitle.vue";
import SvgIcon from "@/components/ui/SvgIcon/SvgIcon.vue";
import { boardIcon, boardTypeName } from "@/model/baseTypes";
import type { Board } from "@/model/board";
import { openBoardInNewTab } from "@/router/navigation";
import { useBoardStore } from "@/store/board";
import { useBoardsStore } from "@/store/boards";
import { injectStrict } from "@/utils/vue";

const boardsStickyMirroredTo = ref<string[]>([]);
const stickyIdsByBoard = ref<Record<string, string>>({});

const card = injectStrict(cardKey);
const cardMeta = injectStrict(cardMetaKey);
const dropdown = injectStrict(dropdownKey);
const cardMethods = injectStrict(cardMethodsKey);
const actionSource = injectStrict(actionSourceKey);

const emit = defineEmits<{ mirror: [] }>();

const { t } = useI18n();

onMounted(async () => {
  const result = await getBoardIdsOfGroupedStickies(card.id);
  boardsStickyMirroredTo.value = result.boardIds;
  stickyIdsByBoard.value = Object.fromEntries(
    result.stickies.map((s) => [s.board_id, s.sticky_id]),
  );
});

const boardsStore = useBoardsStore();
const currentBoardId = computed(() => useBoardStore().currentBoard().id);

const boards = computed(() => {
  const mirroredBoardIds = boardsStickyMirroredTo.value;

  // Boards already mirrored to (excluding current board)
  const mirroredBoards = mirroredBoardIds
    .filter((id) => id !== currentBoardId.value)
    .map((id) => boardsStore.boardById(id));

  // Additional target boards not yet mirrored
  const additionalTargets = boardsStore
    .mirrorTargetBoards([card])
    .filter((board) => !mirroredBoardIds.includes(board.id));

  // Current board
  const currentBoard = boardsStore.boardById(currentBoardId.value);

  const allBoards = [...mirroredBoards, ...additionalTargets];
  const byType = boardsStore.boardTypeOrder;
  allBoards.sort((a, b) => byType(a, b) || boardName(a).localeCompare(boardName(b)));

  return [currentBoard, ...allBoards];
});

const boardName = (board: Board): string => {
  return board.type === "team" ? board.team.name : t(boardTypeName(board.type));
};

const isCurrentBoard = (boardId: string) => {
  return boardId === currentBoardId.value;
};

const isAlreadyMirrored = (boardId: string) => {
  return boardsStickyMirroredTo.value.includes(boardId);
};

const mirrorToBoard = async (targetBoard: Board) => {
  if (cardMeta.isReadonly) return;

  boardsStickyMirroredTo.value.push(targetBoard.id);
  await cardActions.mirror(actionSource, card.id, card.teamId, targetBoard);
  emit("mirror");

  dropdown.close();
  void cardMethods.animate?.("mirroring");
};

const openBoardLink = (board: Board) => {
  const stickyId = stickyIdsByBoard.value[board.id];
  const team = board.type === "team" ? { name: board.team.name } : undefined;
  openBoardInNewTab({ type: board.type, team }, { stickyId });
};
</script>

<template>
  <MenuList class="mirror-sticky" role="dialog" aria-labelledby="mirror-sticky-title">
    <MenuListHeader id="mirror-sticky-title">
      <MenuListTitle>{{ $t("cardAction.mirror") }}</MenuListTitle>
    </MenuListHeader>
    <MenuListItems static :aria-label="$t('cardAction.mirror')" role="list" class="items">
      <ListItem
        v-for="targetBoard in boards"
        :key="targetBoard.id"
        compact
        :data-testid="`mirror-sticky-list-item-${targetBoard.id}`"
        class="item"
      >
        <template #before>
          <SvgIcon :name="boardIcon(targetBoard.type)" width="20" height="20" class="board-icon" />
        </template>
        <span>{{ boardName(targetBoard) }}</span>
        <template #after>
          <template v-if="isCurrentBoard(targetBoard.id)">
            <!-- No button for current board -->
          </template>
          <template v-else-if="isAlreadyMirrored(targetBoard.id)">
            <IconButton
              icon="general/external-link"
              :text="$t('actionMenu.mirrored')"
              data-testid="open-mirrored-board-link"
              class="external-link"
              @click="openBoardLink(targetBoard)"
            />
          </template>
          <template v-else>
            <BaseButton
              variant="outlined"
              color="grey"
              size="small"
              :disabled="cardMeta.isReadonly"
              data-testid="mirror-button"
              @click="mirrorToBoard(targetBoard)"
            >
              {{ $t("cardAction.mirror") }}
            </BaseButton>
          </template>
        </template>
      </ListItem>
    </MenuListItems>
  </MenuList>
</template>

<style lang="scss" scoped>
@use "@/styles/variables/colors";

.mirror-sticky {
  .items {
    padding: 0;

    :deep(.item) {
      height: 42px;
      padding: 8px 12px;

      .before {
        align-self: unset;
        color: colors.$icon-subtle;
      }
    }
  }
}
</style>
